/**
 * Undo スナックバー（SPEC §7.7）。
 * 破壊的操作以外は確認ダイアログを出さず、実行してからここで取り消せるようにする。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** SPEC §7.1: Undo スナックバーは 5 秒表示 */
const DURATION_MS = 5_000;

export interface SnackOptions {
  message: string;
  actionLabel?: string;
  onAction?: () => void | Promise<void>;
  tone?: 'default' | 'error';
}

interface Snack extends SnackOptions {
  id: number;
}

const SnackbarContext = createContext<(o: SnackOptions) => void>(() => {});

export const useSnackbar = (): ((o: SnackOptions) => void) => useContext(SnackbarContext);

export function SnackbarProvider({ children }: { children: ReactNode }): ReactNode {
  const [snack, setSnack] = useState<Snack | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const nextId = useRef(0);

  const show = useCallback((o: SnackOptions) => {
    nextId.current += 1;
    setSnack({ ...o, id: nextId.current });
  }, []);

  useEffect(() => {
    if (!snack) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setSnack(null), DURATION_MS);
    return () => clearTimeout(timer.current);
  }, [snack]);

  const value = useMemo(() => show, [show]);

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {snack && (
        <div
          className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex justify-center px-3
            pb-[calc(1rem+env(safe-area-inset-bottom))]"
          role="status"
          aria-live="polite"
        >
          <div
            key={snack.id}
            className={`pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-2xl border
              px-4 py-3 shadow-2xl shadow-black/50 ${
                snack.tone === 'error'
                  ? 'border-red-500/40 bg-red-950/90 text-red-100'
                  : 'border-ink-700 bg-ink-800/95 text-zinc-100'
              }`}
          >
            <span className="min-w-0 flex-1 text-sm">{snack.message}</span>
            {snack.actionLabel && (
              <button
                type="button"
                className="shrink-0 rounded-lg px-2 py-1 text-sm font-bold text-orange-400
                  hover:bg-white/5"
                onClick={() => {
                  const action = snack.onAction;
                  setSnack(null);
                  void action?.();
                }}
              >
                {snack.actionLabel}
              </button>
            )}
          </div>
        </div>
      )}
    </SnackbarContext.Provider>
  );
}
